import { Versions } from "../actions/types";
import { LoggedOutAction } from "./types";

export const ENABLE_DEFAULT_TERMS = "ENABLE_DEFAULT_TERMS";
export const DISABLE_DEFAULT_TERMS = "DISABLE_DEFAULT_TERMS";
export const TOGGLE_AUTO_PRINTING = "TOGGLE_AUTO_PRINTING";
export const TOGGLE_PRINTING_RECEPTION_INVOICE = "TOGGLE_PRINTING_RECEPTION_INVOICE";
export const SET_PRINTER_FOR_RECEPTION_INVOICE = "SET_PRINTER_FOR_RECEPTION_INVOICE";
export const TOGGLE_SECOND_SCREEN_WINDOW_VISIBLITY = "TOGGLE_SECOND_SCREEN_WINDOW_VISIBLITY";
export const TOGGLE_SHOP_MODE = "TOGGLE_SHOP_MODE";
export const TOGGLE_CUSTOMER_ORDER_MODE = "TOGGLE_CUSTOMER_ORDER_MODE";
export const TOGGLE_SELECT_ORDER_TYPE = "TOGGLE_SELECT_ORDER_TYPE";
export const TOGGLE_CURRENT_SALE_COMPACT_MODE = "TOGGLE_CURRENT_SALE_COMPACT_MODE";
export const TOGGLE_PRE_INVOICE_MODE = "TOGGLE_PRE_INVOICE_MODE";
export const TOGGLE_READ_WEIGHT_FROM_PORT = "TOGGLE_READ_WEIGHT_FROM_PORT";
export const SET_NETWORK_STATUS = "SET_NETWORK_STATUS";
export const SET_PAPER_SIZE = "SET_PAPER_SIZE";
export const SET_LOCALE = "SET_LOCALE";
export const SET_DIRECTION = "SET_DIRECTION";
export const SET_THEME = "SET_THEME";
export const SET_COMPACT = "SET_COMPACT";
export const SET_AUTO_RELEASE_NOTES = "SET_AUTO_RELEASE_NOTES";
export const SET_VERSIONS = "SET_VERSIONS";

export type PrintPaperSizeTypes = "A4" | "A5" | "80mm";
export type LocaleType = "fa" | "en";
export type DirectionType = "rtl" | "ltr";
export type ThemeType = "light" | "dark";

export interface PrinterConfig {
  name: string;
  isDefault: boolean;
}

export interface SettingState {
  autoDisplay: boolean;
  defaultTerms: {
    isEnabled: boolean;
    text: string;
  };
  isAutoPrintingActive: boolean;
  printReceptionInvoice: boolean;
  defaultPrinterForReceptionInvoice?: PrinterConfig;
  isSecondScreenWindowVisible: boolean;
  isShopModeActive: boolean;
  customerOrderMode: boolean;
  showSelectOrderType: boolean;
  fastInvoiceCompactMode: boolean;
  preInvoiceMode: boolean;
  isOnline: boolean;
  readWeightFromPort: boolean;
  paperSize: PrintPaperSizeTypes;
  locale: LocaleType;
  direction: DirectionType;
  versions: Versions;
  theme: ThemeType;
  compactTheme: boolean;
}

export interface EnableDefaultTermsAction {
  type: typeof ENABLE_DEFAULT_TERMS;
  payload: { text: string };
}

export interface DisableDefaultTermsAction {
  type: typeof DISABLE_DEFAULT_TERMS;
}

export interface ToggleAutoPrintingAction {
  type: typeof TOGGLE_AUTO_PRINTING;
}

export interface TogglePrintingReceptionInvoiceAction {
  type: typeof TOGGLE_PRINTING_RECEPTION_INVOICE;
}

export interface SetPrinterForReceptionInvoiceAction {
  type: typeof SET_PRINTER_FOR_RECEPTION_INVOICE;
  payload: { config?: PrinterConfig };
}

export interface ToggleSecondScreenVisiblityAction {
  type: typeof TOGGLE_SECOND_SCREEN_WINDOW_VISIBLITY;
}

export interface ToggleShopModeAction {
  type: typeof TOGGLE_SHOP_MODE;
}

export interface ToggleCustomerOrderModeAction {
  type: typeof TOGGLE_CUSTOMER_ORDER_MODE;
}

export interface ToggleSelectOrderTypeAction {
  type: typeof TOGGLE_SELECT_ORDER_TYPE;
}

export interface ToggleFastInvoiceCompactModeAction {
  type: typeof TOGGLE_CURRENT_SALE_COMPACT_MODE;
}

export interface TogglePreInvoiceModeAction {
  type: typeof TOGGLE_PRE_INVOICE_MODE;
}

export interface ToggleReadWeightFromPortAction {
  type: typeof TOGGLE_READ_WEIGHT_FROM_PORT;
}

export interface SetNetworkStatusAction {
  type: typeof SET_NETWORK_STATUS;
  payload: { isOnline: boolean };
}

export interface SetPaperSizeAction {
  type: typeof SET_PAPER_SIZE;
  payload: { paperSize: PrintPaperSizeTypes };
}

export interface SetLocaleAction {
  type: typeof SET_LOCALE;
  payload: { locale: LocaleType };
}

export interface SetDirectionAction {
  type: typeof SET_DIRECTION;
  payload: { direction: DirectionType };
}

export interface SetThemeAction {
  type: typeof SET_THEME;
  payload: { theme: ThemeType };
}

export interface SetCompactAction {
  type: typeof SET_COMPACT;
  payload: { compact: boolean };
}

export interface SetAutoReleaseNotesAction {
  type: typeof SET_AUTO_RELEASE_NOTES;
  payload: { autoDisplay: boolean };
}

export interface SetVersionsAction {
  type: typeof SET_VERSIONS;
  payload: Versions;
}

export type SettingActionTypes =
  | EnableDefaultTermsAction
  | DisableDefaultTermsAction
  | ToggleAutoPrintingAction
  | TogglePrintingReceptionInvoiceAction
  | SetPrinterForReceptionInvoiceAction
  | ToggleSecondScreenVisiblityAction
  | ToggleShopModeAction
  | ToggleCustomerOrderModeAction
  | ToggleSelectOrderTypeAction
  | ToggleFastInvoiceCompactModeAction
  | TogglePreInvoiceModeAction
  | ToggleReadWeightFromPortAction
  | SetNetworkStatusAction
  | SetPaperSizeAction
  | SetLocaleAction
  | SetDirectionAction
  | SetThemeAction
  | SetCompactAction
  | LoggedOutAction
  | SetVersionsAction
  | SetAutoReleaseNotesAction;

const initialState: SettingState = {
  autoDisplay: true,
  compactTheme: false,
  customerOrderMode: false,
  defaultPrinterForReceptionInvoice: undefined,
  defaultTerms: {
    isEnabled: false,
    text: "",
  },
  direction: "rtl",
  fastInvoiceCompactMode: false,
  isAutoPrintingActive: false,
  isOnline: true,
  isSecondScreenWindowVisible: false,
  isShopModeActive: false,
  locale: "fa",
  paperSize: "A4",
  preInvoiceMode: false,
  printReceptionInvoice: false,
  readWeightFromPort: false,
  showSelectOrderType: false,
  theme: "light",
  versions: { current: "0", latest: "0", supported: "0" },
};

export default function settings(state = initialState, action: SettingActionTypes): SettingState {
  if (action.type === ENABLE_DEFAULT_TERMS) {
    const { text } = action.payload;
    return {
      ...state,
      defaultTerms: {
        isEnabled: true,
        text,
      },
    };
  }

  if (action.type === DISABLE_DEFAULT_TERMS) {
    return {
      ...state,
      defaultTerms: {
        isEnabled: false,
        text: "",
      },
    };
  }
  if (action.type === TOGGLE_AUTO_PRINTING) {
    return {
      ...state,
      isAutoPrintingActive: !state.isAutoPrintingActive,
    };
  }

  if (action.type === TOGGLE_PRINTING_RECEPTION_INVOICE) {
    return {
      ...state,
      printReceptionInvoice: !state.printReceptionInvoice,
    };
  }

  if (action.type === SET_VERSIONS) {
    return {
      ...state,
      versions: action.payload,
    };
  }

  if (action.type === SET_PRINTER_FOR_RECEPTION_INVOICE) {
    const { config: defaultPrinterForReceptionInvoice } = action.payload;
    return {
      ...state,
      defaultPrinterForReceptionInvoice,
    };
  }

  if (action.type === TOGGLE_SECOND_SCREEN_WINDOW_VISIBLITY) {
    return {
      ...state,
      isSecondScreenWindowVisible: !state.isSecondScreenWindowVisible,
    };
  }

  if (action.type === TOGGLE_SHOP_MODE) {
    return {
      ...state,
      isShopModeActive: !state.isShopModeActive,
    };
  }

  if (action.type === TOGGLE_CUSTOMER_ORDER_MODE) {
    return {
      ...state,
      customerOrderMode: !state.customerOrderMode,
    };
  }

  if (action.type === TOGGLE_SELECT_ORDER_TYPE) {
    return {
      ...state,
      showSelectOrderType: !state.showSelectOrderType,
    };
  }

  if (action.type === TOGGLE_CURRENT_SALE_COMPACT_MODE) {
    return {
      ...state,
      fastInvoiceCompactMode: !state.fastInvoiceCompactMode,
    };
  }

  if (action.type === TOGGLE_PRE_INVOICE_MODE) {
    return {
      ...state,
      preInvoiceMode: !state.preInvoiceMode,
    };
  }

  if (action.type === TOGGLE_READ_WEIGHT_FROM_PORT) {
    return {
      ...state,
      readWeightFromPort: !state.readWeightFromPort,
    };
  }

  if (action.type === SET_NETWORK_STATUS) {
    const { isOnline } = action.payload;
    return {
      ...state,
      isOnline,
    };
  }

  if (action.type === SET_PAPER_SIZE) {
    const { paperSize } = action.payload;
    return {
      ...state,
      paperSize: paperSize !== "A4" && paperSize !== "A5" && paperSize !== "80mm" ? initialState.paperSize : paperSize,
    };
  }

  if (action.type === SET_LOCALE) {
    const { locale } = action.payload;
    return {
      ...state,
      locale,
    };
  }

  if (action.type === SET_DIRECTION) {
    const { direction } = action.payload;
    return {
      ...state,
      direction,
    };
  }

  if (action.type === SET_THEME) {
    const { theme } = action.payload;
    return {
      ...state,
      theme,
    };
  }

  if (action.type === SET_COMPACT) {
    const { compact } = action.payload;
    return {
      ...state,
      compactTheme: compact,
    };
  }

  if (action.type === SET_AUTO_RELEASE_NOTES) {
    const { autoDisplay } = action.payload;
    return {
      ...state,
      autoDisplay,
    };
  }

  // Don't reset app settings on logout
  // if (action.type === LOGGED_OUT) {
  //   return initialState;
  // }

  return state;
}
